import type { Tour, UserSegment } from '@/types/product-tour';

// Define all product tours
// All targets reference data-tour attributes added in Phase 2.
// Rules:
//   - Max 5 steps per tour
//   - No emoji in titles
//   - First step: placement 'center', no target
//   - Last step: placement 'center', no target
//   - estimatedDuration ≈ 10–12s per step

export const tours: Tour[] = [
  // ─── Task 3.1 — Welcome Tour ───────────────────────────────────────────────
  {
    id: 'welcome-tour',
    name: 'Welcome to Time',
    description: 'A quick orientation to get you up and running in under a minute.',
    trigger: 'first_login',
    priority: 100,
    isOptional: false,
    estimatedDuration: 50,
    pages: ['*'],
    segment: ['new_user'],
    steps: [
      {
        id: 'welcome-1',
        title: 'Welcome to Time',
        description:
          'This short tour will show you the essentials — navigation, your timesheet status, and how to get help. It takes less than a minute.',
        placement: 'center',
        disableBeacon: true,
        showSkip: true,
        showPrevious: false,
      },
      {
        id: 'welcome-2',
        title: 'Main Navigation',
        description:
          'The sidebar gives you access to every part of the app — your timesheet, approvals, reports, and settings. Click any item to navigate there.',
        placement: 'center',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'welcome-3',
        title: 'Your Timesheet Status',
        description:
          'This card shows your current week at a glance — hours logged, submission status, and any outstanding actions you need to take.',
        placement: 'center',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'welcome-4',
        title: 'Help and Tours',
        description:
          'Click this button at any time to access help articles, restart this tour, or launch a guided walkthrough of any feature.',
        placement: 'center',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'welcome-5',
        title: 'You Are Ready to Go',
        description:
          'That covers the basics. Start by logging your first timesheet — head to the Timesheet page from the sidebar whenever you are ready.',
        placement: 'center',
        disableBeacon: true,
        showSkip: false,
        showPrevious: true,
      },
    ],
  },

  // ─── Task 3.2 — Timesheet Tour ─────────────────────────────────────────────
  {
    id: 'timesheet-tour',
    name: 'Logging Your Time',
    description: 'Learn how to add, edit, and submit your weekly timesheet.',
    trigger: 'feature_discovery',
    priority: 90,
    isOptional: true,
    estimatedDuration: 90,
    pages: ['/timesheet'],
    segment: ['new_user', 'employee'],
    steps: [
      {
        id: 'timesheet-1',
        title: 'Logging Your Time',
        description:
          'This page is where you record your hours each week. We will walk through every key control — from entering hours to submitting for approval.',
        placement: 'center',
        disableBeacon: true,
        showSkip: true,
        showPrevious: false,
      },
      {
        id: 'timesheet-2',
        title: 'The Timesheet Grid',
        description:
          'Each row represents a project or work type. Columns across the top represent days of the week. Your total hours are calculated automatically as you enter time.',
        target: '[data-tour="timesheet-grid"]',
        placement: 'top',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'timesheet-3',
        title: 'Entering Your Hours',
        description:
          'Click any cell to enter your hours for that day. Type a number and press Tab to move to the next cell. You can enter decimals — for example, 1.5 for an hour and a half.',
        target: '[data-tour="timesheet-hours-cell"]',
        placement: 'bottom',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'timesheet-4',
        title: 'Row Actions',
        description:
          'Click the three-dot menu on any row to duplicate it, delete it, or view details for scheduled work or absences.',
        target: '[data-tour="timesheet-row-actions"]',
        placement: 'left',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'timesheet-5',
        title: 'Adding a New Row',
        description:
          'Click "Add Row" to add another project or work type to your timesheet. You can have as many rows as you need.',
        target: '[data-tour="timesheet-add-row"]',
        placement: 'top',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'timesheet-6',
        title: 'Switching Weeks',
        description:
          'Use the week selector to move between weeks. All your previous timesheets are saved and accessible here — you can also edit past weeks if they are still open.',
        target: '[data-tour="timesheet-week-nav"]',
        placement: 'bottom',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'timesheet-7',
        title: 'Your Submission Status',
        description:
          'The status badge shows where your timesheet stands — Draft, Submitted, Approved, or Rejected. A rejected timesheet will include a comment from your manager explaining what needs to change.',
        target: '[data-tour="timesheet-status"]',
        placement: 'bottom',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'timesheet-8',
        title: 'Timesheet History',
        description:
          'Click the History button to view a full audit trail of your timesheet — every submission, approval, rejection, and change is logged here with timestamps. Useful if you need to understand why a timesheet was rejected or track what changed.',
        target: '[data-tour="timesheet-history"]',
        placement: 'bottom',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'timesheet-9',
        title: 'Submitting for Approval',
        description:
          'Once your hours are complete, click "Submit for Approval". Your manager will be notified to review your timesheet. You cannot edit a submitted timesheet unless it is sent back to you or you recall it. The recall button will be available on submitted timesheets before they are approved, you can find it in the same section where the Submit button lives.',
        target: '[data-tour="timesheet-submit"]',
        placement: 'left',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'timesheet-10',
        title: 'You Are Ready to Log Time',
        description:
          'That covers everything you need to know. Fill in your hours, add rows for each project, and submit before your deadline. If you get stuck, open the Help menu at any time.',
        placement: 'center',
        disableBeacon: true,
        showSkip: false,
        showPrevious: true,
      },
    ],
  },

  // ─── Task 3.3 — Approvals Tour ─────────────────────────────────────────────
  {
    id: 'approvals-tour',
    name: 'Reviewing Your Team\'s Timesheets',
    description: 'Learn how to review, filter, and approve timesheets submitted by your team.',
    trigger: 'feature_discovery',
    priority: 85,
    isOptional: true,
    estimatedDuration: 80,
    pages: ['/approvals'],
    segment: ['manager', 'admin'],
    steps: [
      {
        id: 'approvals-1',
        title: 'Reviewing Your Team\'s Timesheets',
        description:
          'This page shows all timesheets awaiting your approval. You can review, approve, or reject individual submissions — or handle multiple at once. We will walk through each control.',
        placement: 'center',
        disableBeacon: true,
        showSkip: true,
        showPrevious: false,
      },
      {
        id: 'approvals-2',
        title: 'Pending Submissions',
        description:
          'Each row shows a team member\'s timesheet — their name, the week covered, total hours logged, and current status. Click any row to open the full day-by-day breakdown before making a decision.',
        target: '[data-tour="approvals-first-row"]',
        placement: 'top',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'approvals-3',
        title: 'Filtering Submissions',
        description:
          'Use the filters to narrow the list by department, anomaly flags, or overdue status. Anomaly flags highlight unusual entries — such as very high hours or missing days — that may need a closer look.',
        target: '[data-tour="approvals-filter"]',
        placement: 'bottom',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'approvals-4',
        title: 'Selecting Timesheets',
        description:
          'Use the checkbox in the column header to select all visible timesheets at once, or tick individual rows to build your selection. Combined with your active filters, this lets you clear a full inbox in just a few clicks.',
        target: '[data-tour="approvals-bulk-select"]',
        placement: 'right',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'approvals-5',
        title: 'Rejecting Timesheets',
        description:
          'Click "Reject Selected" to send timesheets back to the submitter. You will be prompted to add a reason — this helps your team understand what needs to be corrected before resubmitting.',
        placement: 'center',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'approvals-6',
        title: 'You Are Ready to Review',
        description:
          'That covers everything on the Approvals page. Use the filters to focus your review, select timesheets in bulk, and approve or reject with a single click.',
        placement: 'center',
        disableBeacon: true,
        showSkip: false,
        showPrevious: true,
      },
    ],
  },

  // ─── Task 3.4 — Employee Dashboard Tour ────────────────────────────────────
  {
    id: 'dashboard-employee-tour',
    name: 'Your Homepage',
    description: 'A quick look at the key information on your homepage.',
    trigger: 'feature_discovery',
    priority: 80,
    isOptional: true,
    estimatedDuration: 40,
    pages: ['/'],
    segment: ['employee'],
    steps: [
      {
        id: 'dashboard-employee-1',
        title: 'Your Homepage',
        description:
          'Your homepage gives you an instant view of your timesheet status and any actions that need your attention this week.',
        placement: 'center',
        disableBeacon: true,
        showSkip: true,
        showPrevious: false,
      },
      {
        id: 'dashboard-employee-2',
        title: 'This Week\'s Summary',
        description:
          'This card shows your current week — hours logged so far, the submission status (draft, submitted, approved), and your deadline.',
        target: '[data-tour="homepage-summary"]',
        placement: 'bottom',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'dashboard-employee-3',
        title: 'Alerts and Notices',
        description:
          'Any urgent items appear here — overdue timesheets, rejected submissions, or upcoming deadlines. Address these first to keep your record up to date.',
        target: '[data-tour="homepage-alerts"]',
        placement: 'bottom',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'dashboard-employee-4',
        title: 'Your Performance Metrics',
        description:
          'Scroll down to see your key numbers — billable hours, monthly totals, average weekly hours, and your utilisation rate. These update automatically as you log time.',
        target: '[data-tour="homepage-performance"]',
        placement: 'top',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'dashboard-employee-5',
        title: 'Go to Your Timesheet',
        description:
          'Click "View Timesheet" to jump straight to the current week. It is the fastest way to log or update your hours.',
        target: '[data-tour="homepage-view-timesheet"]',
        placement: 'right',
        disableBeacon: true,
        showSkip: false,
        showPrevious: true,
      },
    ],
  },

  // ─── Task 3.5 — Manager Dashboard Tour ─────────────────────────────────────
  {
    id: 'dashboard-manager-tour',
    name: 'Your Homepage',
    description: 'Understand your team-level summary and alerts at a glance.',
    trigger: 'feature_discovery',
    priority: 80,
    isOptional: true,
    estimatedDuration: 40,
    pages: ['/', '/manager'],
    segment: ['manager', 'admin'],
    steps: [
      {
        id: 'dashboard-manager-1',
        title: 'Your Homepage',
        description:
          'As a manager, your homepage shows a team-level view — submission progress across your team, outstanding approvals, and any alerts that need your attention.',
        placement: 'center',
        disableBeacon: true,
        showSkip: true,
        showPrevious: false,
      },
      {
        id: 'dashboard-manager-2',
        title: 'Team Submission Overview',
        description:
          'This card summarises your team\'s timesheet status for the current week — how many have submitted, how many are still outstanding, and the overall completion rate.',
        target: '[data-tour="homepage-summary"]',
        placement: 'bottom',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'dashboard-manager-3',
        title: 'Team Alerts',
        description:
          'Team-level alerts appear here — missing timesheets, overdue submissions, or anything flagged for your attention. These update in real time as your team takes action.',
        target: '[data-tour="homepage-alerts"]',
        placement: 'bottom',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'dashboard-manager-4',
        title: 'Key Metrics at a Glance',
        description:
          'These four cards give you an instant team health check — pending approvals, missing timesheets, total hours logged, and overall utilisation rate. Click any card to drill straight into the detail.',
        target: '[data-tour="homepage-key-metrics"]',
        placement: 'bottom',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'dashboard-manager-5',
        title: 'Next Step: Approvals',
        description:
          'Head to the Approvals page from the sidebar to review and approve pending timesheets from your team. You can approve individually or in bulk.',
        placement: 'center',
        disableBeacon: true,
        showSkip: false,
        showPrevious: true,
      },
    ],
  },

  // ─── Task 3.6 — Reports Tour ────────────────────────────────────────────────
  {
    id: 'reports-tour',
    name: 'Timesheet Reports',
    description: 'Learn how to filter, drill down, and export timesheet data from the Timesheet Reports page.',
    trigger: 'feature_discovery',
    priority: 70,
    isOptional: true,
    estimatedDuration: 80,
    pages: [
      '/reports/timesheet',
    ],
    segment: ['manager', 'admin'],
    steps: [
      {
        id: 'reports-1',
        title: 'Timesheet Reports',
        description:
          'The Timesheet Reports page lets you view, filter, and export timesheet submissions across your team. We will walk through the key controls.',
        placement: 'center',
        disableBeacon: true,
        showSkip: true,
        showPrevious: false,
      },
      {
        id: 'reports-2',
        title: 'Advanced Filters',
        description:
          'Use the Advanced Filters option to narrow results by department, cost centre, work type, and more. Combined with the date range filter, you can pinpoint exactly the data you need.',
        target: '[data-tour="reports-advanced-filters"]',
        placement: 'bottom',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'reports-3',
        title: 'Filtering by Date Range',
        description:
          'Click the "All Time" dropdown to adjust the time filter — select a quick range such as This Month or Last Month, or set a custom start and end date to narrow the report to the exact period you need.',
        target: '[data-tour="reports-date-filter"]',
        placement: 'bottom',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'reports-4',
        title: 'The Results Table',
        description:
          'The table shows all data matching your active filters. Click any column header to sort by that column. Click a row to drill down into an individual\'s full timesheet detail for the period.',
        target: '[data-tour="reports-results-table"]',
        placement: 'top',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'reports-5',
        title: 'Exporting Data',
        description:
          'Click the export button to download the current report as an Excel file. The export respects all your active filters — what you see in the table is exactly what you get in the file.',
        target: '[data-tour="reports-export"]',
        placement: 'left',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'reports-6',
        title: 'You Are Ready to Use Timesheet Reports',
        description:
          'Use Timesheet Reports to review submissions, check for gaps, and export data at the end of each pay period.',
        placement: 'center',
        disableBeacon: true,
        showSkip: false,
        showPrevious: true,
      },
    ],
  },

  // ─── Task 3.7 — Missing Timesheets Tour ────────────────────────────────────
  {
    id: 'missing-timesheets-tour',
    name: 'Chasing Missing Timesheets',
    description: 'Learn how to identify and follow up on team members who have not submitted their timesheets.',
    trigger: 'feature_discovery',
    priority: 65,
    isOptional: true,
    estimatedDuration: 40,
    pages: ['/reports/missing-timesheets'],
    segment: ['manager', 'admin'],
    steps: [
      {
        id: 'missing-1',
        title: 'Chasing Missing Timesheets',
        description:
          'This report shows every team member who has not submitted a timesheet for the selected period. Use it to identify gaps and follow up quickly.',
        placement: 'center',
        disableBeacon: true,
        showSkip: true,
        showPrevious: false,
      },
      {
        id: 'missing-2',
        title: 'Who Is Missing',
        description:
          'Each row shows a team member who has not submitted, along with the period they are missing and how many days overdue they are. The list is sorted by most overdue first.',
        target: '[data-tour="reports-results-table"]',
        placement: 'top',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'missing-4',
        title: 'Following Up',
        description:
          'Once you have identified who is missing, reach out directly or use your organisation\'s reminder process. Keep this report bookmarked — checking it weekly helps prevent backlogs.',
        placement: 'center',
        disableBeacon: true,
        showSkip: false,
        showPrevious: true,
      },
    ],
  },

  // ─── My Delegations Tour ───────────────────────────────────────────────────
  {
    id: 'my-delegations-tour',
    name: 'My Delegations',
    description: 'Learn how to delegate your approval responsibilities when you are unavailable.',
    trigger: 'feature_discovery',
    priority: 60,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/approvals/delegations'],
    segment: ['manager', 'admin'],
    steps: [
      {
        id: 'delegation-1',
        title: 'Approval Delegations',
        description:
          'Delegations let you hand over your approval responsibilities to a colleague for a set period — useful for holidays, leave, or any time you are unavailable to approve timesheets.',
        placement: 'center',
        disableBeacon: true,
        showSkip: true,
        showPrevious: false,
      },
      {
        id: 'delegation-2',
        title: 'Setting Up a Delegation',
        description:
          'Click "Set Up Delegation" to choose a delegate, set the date range, and add any notes. Your delegate will be able to approve timesheets on your behalf for that period.',
        target: '[data-tour="delegation-create"]',
        placement: 'bottom',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'delegation-3',
        title: 'Your Active Delegations',
        description:
          'This table shows all your current and past delegations — who you delegated to, the date range, duration, and current status. Active delegations are live right now.',
        target: '[data-tour="delegation-table"]',
        placement: 'top',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'delegation-4',
        title: 'Filtering by Status',
        description:
          'Use the status filter to view only active, expired, or revoked delegations. This helps you keep track of what is currently in effect and review historical delegations.',
        target: '[data-tour="delegation-filter"]',
        placement: 'bottom',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'delegation-5',
        title: 'Exporting Your Delegations',
        description:
          'Export your full delegation history as a CSV for record-keeping or auditing purposes. The export includes all statuses and date ranges.',
        target: '[data-tour="delegation-export"]',
        placement: 'bottom',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'delegation-6',
        title: 'Revoking a Delegation',
        description:
          'If plans change, you can revoke an active delegation at any time using the Revoke button on any active row. The delegate will immediately lose approval access.',
        placement: 'center',
        disableBeacon: true,
        showSkip: false,
        showPrevious: true,
      },
    ],
  },

  // ─── Utilisation Report Tour ───────────────────────────────────────────────
  {
    id: 'utilisation-report-tour',
    name: 'Utilisation Report',
    description: 'Understand how to read and use the utilisation report to track team efficiency.',
    trigger: 'feature_discovery',
    priority: 65,
    isOptional: true,
    estimatedDuration: 70,
    pages: ['/reports/utilisation'],
    segment: ['manager', 'admin'],
    steps: [
      {
        id: 'utilisation-1',
        title: 'Utilisation Report',
        description:
          'The Utilisation Report shows how effectively your team is using their available time. It breaks down billable vs non-billable hours, highlights over- and under-utilised team members, and tracks trends over time.',
        placement: 'center',
        disableBeacon: true,
        showSkip: true,
        showPrevious: false,
      },
      {
        id: 'utilisation-2',
        title: 'Filters',
        description:
          'Start by selecting a date range and filtering by department. This scopes all the charts and metrics below to your chosen period and team.',
        target: '[data-tour="reports-date-filter"]',
        placement: 'bottom',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'utilisation-3',
        title: 'Key Performance Indicators',
        description:
          'These KPI cards give you an at-a-glance summary — total hours logged, average utilisation percentage, billable ratio, and team capacity. Click any card to jump to the related section below.',
        target: '[data-tour="utilisation-kpis"]',
        placement: 'bottom',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'utilisation-4',
        title: 'Weekly Trends',
        description:
          'The trends chart shows how utilisation has changed week by week over the selected period. Look for patterns — dips often indicate bank holidays, leave clusters, or project gaps.',
        target: '[data-tour="utilisation-trends"]',
        placement: 'top',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'utilisation-5',
        title: 'Daily Utilisation Chart',
        description:
          'This chart shows utilisation broken down by day. It is the most granular view — useful for spotting specific days where the team was under capacity or unusually busy.',
        target: '[data-tour="utilisation-chart"]',
        placement: 'top',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'utilisation-6',
        title: 'You Are Ready to Use the Utilisation Report',
        description:
          'Review this report regularly to stay ahead of capacity issues and spot trends across your team.',
        placement: 'center',
        disableBeacon: true,
        showSkip: false,
        showPrevious: true,
      },
    ],
  },

  // ─── Team Members Tour ────────────────────────────────────────────────────
  {
    id: 'team-members-tour',
    name: 'Team Members',
    description: 'Learn how to navigate and use the team members directory.',
    trigger: 'feature_discovery',
    priority: 55,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/team/members'],
    segment: ['manager', 'admin'],
    steps: [
      {
        id: 'team-1',
        title: 'Team Members Directory',
        description:
          'The Team Members page is your central directory for everyone in the organisation. You can search, filter, and view detailed profiles — including contact details, department, role, and working pattern.',
        placement: 'center',
        disableBeacon: true,
        showSkip: true,
        showPrevious: false,
      },
      {
        id: 'team-2',
        title: 'Headcount Overview',
        description:
          'These metric cards give you an instant summary of your workforce — total headcount, how many are active, how many are inactive, and which department is the largest.',
        target: '[data-tour="team-stats"]',
        placement: 'bottom',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'team-3',
        title: 'Search, Filter & Columns',
        description:
          'Search by name or employee code. Click the Filter button to open a drawer where you can narrow results by department, role, status, and change the sort order. In list view, click Columns to choose which fields are displayed. Switch between grid and list view using the icons on the right.',
        target: '[data-tour="team-search-filter"]',
        placement: 'bottom',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'team-4',
        title: 'Team Member Cards',
        description:
          'Each card shows the team member\'s photo, name, role, department, and status. Click any card to open their full profile — including contact details, working pattern, and timesheet history.',
        target: '[data-tour="team-member-grid"]',
        placement: 'top',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'team-5',
        title: 'You Are Ready to Use Team Members',
        description:
          'Use the Team Members page to look up colleagues, check working patterns before scheduling, and monitor headcount by department. Click any member card to view their full profile.',
        placement: 'center',
        disableBeacon: true,
        showSkip: false,
        showPrevious: true,
      },
    ],
  },

  // ─── Active Projects Tour ─────────────────────────────────────────────────
  {
    id: 'active-projects-tour',
    name: 'Active Projects',
    description: 'Learn how to use the Active Projects page to monitor your project portfolio.',
    trigger: 'feature_discovery',
    priority: 55,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/projects/active'],
    segment: ['manager', 'admin'],
    steps: [
      {
        id: 'projects-1',
        title: 'Active Projects',
        description:
          'The Active Projects page gives you a full view of your project portfolio — including status, priority, budget, team size, and key dates. Use it to monitor project health and quickly identify anything that needs attention.',
        placement: 'center',
        disableBeacon: true,
        showSkip: true,
        showPrevious: false,
      },
      {
        id: 'projects-2',
        title: 'Portfolio Summary',
        description:
          'These metric cards summarise your portfolio at a glance — total projects, team members across all projects, how many are at risk, and how many are high priority. Click a card to filter the list below.',
        target: '[data-tour="projects-stats"]',
        placement: 'bottom',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'projects-3',
        title: 'Search and Filter',
        description:
          'Search by project name, code, or client. Filter by status (active, at risk, on hold) and priority (high, medium, low). Use the sort options to order by name, date, or client. Combine filters to pinpoint exactly what you need.',
        target: '[data-tour="projects-filter"]',
        placement: 'bottom',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'projects-4',
        title: 'Project Cards',
        description:
          'Each project card shows the project name, client, status badge, priority, team size, and key dates. At-risk projects are highlighted with an amber border. Click any card to open the full project details including team members and budget breakdown.',
        target: '[data-tour="projects-grid"]',
        placement: 'top',
        disableBeacon: true,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'projects-5',
        title: 'You Are Ready to Use Active Projects',
        description:
          'Check this page regularly to stay on top of your project portfolio. Filter by "At Risk" to focus on projects that need attention, and click through to any project for the full picture.',
        placement: 'center',
        disableBeacon: true,
        showSkip: false,
        showPrevious: true,
      },
    ],
  },
];

// Helper function to get tour by ID
export const getTourById = (tourId: string): Tour | undefined => {
  return tours.find(tour => tour.id === tourId);
};

// Helper function to get tours by segment
export const getToursBySegment = (segment: string): Tour[] => {
  return tours.filter(tour =>
    !tour.segment || tour.segment.includes(segment as any)
  ).sort((a, b) => b.priority - a.priority);
};

// Helper function to get tours by trigger
export const getToursByTrigger = (trigger: string): Tour[] => {
  return tours.filter(tour => tour.trigger === trigger)
    .sort((a, b) => b.priority - a.priority);
};

// Helper function to get tours relevant to a specific page path and user segment.
// Returns tours where pages includes the given path OR includes '*' (global tours).
// If a userSegment is provided, tours with a segment definition must include that segment.
// Tours with no pages defined are treated as global.
// Tours with no segment defined are shown to all users.
export const getToursByPage = (path: string, userSegment?: UserSegment): Tour[] => {
  return tours.filter(tour => {
    // Page filter
    const pageMatch = !tour.pages || tour.pages.length === 0
      ? true
      : tour.pages.includes('*') || tour.pages.includes(path);

    if (!pageMatch) return false;

    // Segment filter — if tour has no segment, show to everyone
    if (!userSegment || !tour.segment || tour.segment.length === 0) return true;

    return tour.segment.includes(userSegment);
  }).sort((a, b) => b.priority - a.priority);
};
